import { useCallback, useMemo, useState } from "react";
import { api } from "../api/client";
import { useConflictGate, type ConflictGate } from "./useConflictGate";
import type { Booking } from "./bookingTypes";

/**
 * useMoveMode — режим переноса брони ПРЯМО В ОБЫЧНОЙ СЕТКЕ DC (A48-коррекция
 * 13.08: владелец отверг отдельный интерфейс MoveGrid — «Перенести» включает
 * move-режим основной сетки: баннер сверху, дата/филиалы листаются штатной
 * шапкой, тап по свободному слоту любого квеста = перенос).
 *
 * dropOnSlot шлёт PUT /api/bookings/move (BM5: quest_id, пересчёт выручки,
 * переназначение чайной, ночной сдвиг — всё серверное) через useConflictGate:
 * 409 с conflicts → хост рендерит ConflictOverride (gate.conflicts) с force-
 * повтором «ВСЁ РАВНО ПЕРЕНЕСТИ». Тосты чайной — по ответу /move.
 */
export interface MoveMode {
  /** Переносимая бронь; null — режим выключен. */
  booking: Booking | null;
  /** true — PUT /move в полёте (13.08: сетка под вуалью, баннер «ПЕРЕНОСИМ…»). */
  moving: boolean;
  start: (b: Booking) => void;
  cancel: () => void;
  /** Тап по свободному слоту сетки: перенос на (questId, time, дата сетки). */
  /** customTime — 17.09: тап по слоту, сдвинутому МК: бронь едет на 17:45 как нестандартная (time = custom_time). */
  dropOnSlot: (questId: string, time: string, dateStr: string, customTime?: string | null) => Promise<void>;
  /** Force-повтор 409 с той же индикацией полёта. */
  forceRetry: () => Promise<void>;
  gate: ConflictGate;
}

interface MoveResp { ok: boolean; tea_reassigned?: true; tea_removed?: true }

export function useMoveMode(
  onToast: (m: string) => void,
  onDone: () => void,
): MoveMode {
  const [booking, setBooking] = useState<Booking | null>(null);
  const [moving, setMoving] = useState(false);
  const gate = useConflictGate((err) =>
    onToast(err instanceof Error ? err.message : "Ошибка переноса"),
  );

  const start = useCallback((b: Booking) => setBooking(b), []);
  const cancel = useCallback(() => {
    setBooking(null);
    gate.clear();
  }, [gate]);

  const forceRetry = useCallback(async () => {
    setMoving(true);
    try { await gate.retry(); } finally { setMoving(false); }
  }, [gate]);

  const dropOnSlot = useCallback(
    async (questId: string, time: string, dateStr: string, customTime?: string | null) => {
      if (!booking?.id) return;
      setMoving(true);
      try {
        await gate.run(async (force?: boolean) => {
          const r = await api<MoveResp>("/api/bookings/move", {
            method: "PUT",
            body: { id: booking.id, date: dateStr, time: customTime || time, quest_id: questId, custom: customTime ? true : undefined, force: force || undefined },
          });
          onToast(
            r.tea_removed ? "⚠ Перенесено · чайная снята (в филиале нет чайных)"
            : r.tea_reassigned ? "✦ Перенесено · чайная переназначена"
            : "✦ Бронь перенесена",
          );
          setBooking(null);
          onDone();
        }, "ВСЁ РАВНО ПЕРЕНЕСТИ");
      } catch (err) {
        onToast(err instanceof Error ? err.message : "Ошибка переноса");
      } finally {
        setMoving(false);
      }
    },
    [booking, gate, onToast, onDone],
  );

  return useMemo(
    () => ({ booking, moving, start, cancel, dropOnSlot, forceRetry, gate }),
    [booking, moving, start, cancel, dropOnSlot, forceRetry, gate],
  );
}
